import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import {
  ArrowLeft,
  CreditCard,
  Landmark,
  Banknote,
  ShieldCheck,
  Hourglass,
} from 'lucide-react';
import useAuth from '../../../hooks/useAuth';
import useCobroStore from '../../../store/cobro';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Skeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import {
  formatCurrency,
  formatDate,
  CHARGE_MODE_LABELS,
} from '../../adminView/cobro/utils/cobroHelpers';
import { amountDueOf, paidAmountOf } from '../../adminView/cobro/utils/payments';
import { gatewayMeta } from './paymentMethods';

// Identidad estable de un método: la pasarela usa su provider; los no-PSP, su type.
const methodId = (m) => (m.type === 'gateway' ? m.provider : m.type);
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const NONPSP_META = {
  manual_transfer: {
    icon: Landmark,
    label: 'Transferencia manual',
    description:
      'Transfiere a la cuenta de tu operador y sube tu comprobante. El pago queda sujeto a su confirmación.',
  },
  cash: {
    icon: Banknote,
    label: 'Efectivo',
    description:
      'Paga en efectivo a tu operador. El pago queda sujeto a su confirmación.',
  },
};

// Tarjeta-radio de un método de pago (pasarela, transferencia o efectivo).
const MethodCard = ({ method, selected, onSelect }) => {
  const { t } = useTranslation();
  const isGateway = method.type === 'gateway';
  const meta = isGateway ? gatewayMeta(method.provider) : NONPSP_META[method.type];
  const value = methodId(method);
  const [logoOk, setLogoOk] = useState(Boolean(isGateway && meta?.logo));
  const Icon = !isGateway ? meta?.icon ?? CreditCard : CreditCard;
  // Los logos de marca están hechos para fondo claro: sobre el tile van en blanco
  // (con borde), así contrastan también en modo oscuro (Webpay/MercadoPago/Linkify).
  const showLogo = isGateway && meta?.logo && logoOk;
  if (!meta) return null;
  return (
    <label
      className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
        selected
          ? 'border-brand-500 bg-brand-50'
          : 'border-border bg-bg hover:bg-bg-muted'
      }`}
    >
      <input
        type="radio"
        name="paymentMethod"
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
        className="mt-1 w-4 h-4 accent-brand-500 shrink-0"
      />
      <span
        className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 overflow-hidden ${
          showLogo ? "bg-white border border-border" : "bg-bg-subtle"
        }`}
      >
        {showLogo ? (
          <img
            src={meta.logo}
            alt={t(meta.label)}
            className="w-7 h-7 object-contain dark-safe-img"
            onError={() => setLogoOk(false)}
          />
        ) : (
          <Icon className="w-5 h-5 text-fg-muted" aria-hidden />
        )}
      </span>
      <span className="min-w-0">
        <span
          className={`block font-medium ${selected ? 'text-brand-700' : 'text-fg'}`}
        >
          {t(meta.label)}
        </span>
        {meta.description && (
          <span className="block text-sm text-fg-muted mt-0.5">
            {t(meta.description)}
          </span>
        )}
      </span>
    </label>
  );
};

const PaymentMethodSelect = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const { getMyNote, fetchPaymentMethods, pay, saving, submitCash, submittingTransfer } =
    useCobroStore((s) => ({
      getMyNote: s.getMyNote,
      fetchPaymentMethods: s.fetchPaymentMethods,
      pay: s.pay,
      saving: s.saving,
      submitCash: s.submitCash,
      submittingTransfer: s.submittingTransfer,
    }));

  const [note, setNote] = useState(null);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState('');

  const backToNote = () => navigate(`/home/pagos/${id}`);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getMyNote(id), fetchPaymentMethods(id)])
      .then(([noteData, methodsData]) => {
        if (!active) return;
        setNote(noteData);
        const list = methodsData?.methods ?? [];
        setMethods(list);
        if (list.length === 1) setSelected(methodId(list[0]));
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ?? t('No se pudieron cargar los métodos de pago.'),
        );
        backToNote();
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const userEmail = user?.email;
  // Depende del email, no del objeto `user`: un provider que entrega un objeto nuevo en
  // cada render re-dispararía el efecto sin fin (setEmail con el mismo valor).
  useEffect(() => {
    if (userEmail) setEmail(userEmail);
  }, [userEmail]);

  const selectedMethod = useMemo(
    () => methods.find((m) => methodId(m) === selected) ?? null,
    [methods, selected],
  );
  const isGateway = selectedMethod?.type === 'gateway';
  const isCash = selectedMethod?.type === 'cash';
  const needsEmail = isGateway || isCash;
  const selectedMeta = isGateway ? gatewayMeta(selectedMethod.provider) : null;

  const isPaid = note?.paymentStatus === 'paid';
  const isCancelled =
    note?.status === 'cancelled' || note?.paymentStatus === 'cancelled';
  const isInReview = note?.paymentStatus === 'in_review';
  // El comercio paga el SALDO vigente, no el total de la nota (que puede tener abonos).
  const amountDue = note ? amountDueOf(note) : 0;
  const amountPaid = note ? paidAmountOf(note) : 0;
  const nothingToPay = !!note && !isPaid && !isCancelled && !isInReview && amountDue <= 0;

  const handleContinue = async () => {
    if (!selected) {
      toast.error(t('Elige un método de pago para continuar.'));
      return;
    }
    // Transferencia manual: el detalle (datos bancarios + comprobante) va aparte.
    if (selectedMethod?.type === 'manual_transfer') {
      navigate(`/home/pagos/${id}/transferencia`);
      return;
    }
    const trimmed = email.trim();
    if (needsEmail && !emailOk(trimmed)) {
      toast.error(t('Ingresa un correo válido para enviarte el comprobante.'));
      return;
    }
    // Efectivo: informa el pago por el saldo vigente (queda en revisión) y va al resultado.
    if (isCash) {
      try {
        await submitCash({ noteId: note._id, email: trimmed, amount: amountDue });
        navigate(`/home/pagos/${id}/resultado`);
      } catch (err) {
        toast.error(
          err?.response?.data?.message ?? t('No se pudo informar el pago en efectivo.'),
        );
      }
      return;
    }
    // Pasarela: inicia el cargo y redirige al PSP.
    try {
      const res = await pay(note._id, trimmed, selected);
      if (res?.paymentUrl) {
        window.location.assign(res.paymentUrl);
      } else {
        toast.error(t('No se recibió el enlace de pago. Intenta nuevamente.'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? t('No se pudo iniciar el pago.'));
    }
  };

  if (loading || !note) {
    return (
      <div className="bill bill-page" style={{ padding: 16 }} role="status" aria-live="polite">
        <Skeleton className="h-4 w-32" style={{ marginBottom: '1rem' }} />
        <div className="bill-card bill-card--pad">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-40" style={{ marginTop: '0.5rem' }} />
        </div>
        <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" style={{ marginBottom: '0.75rem' }} />
          ))}
        </div>
      </div>
    );
  }

  const resolved = isPaid || isCancelled || isInReview || nothingToPay;

  return (
    <div className="bill bill-page" style={{ padding: 16 }}>
      <button type="button" className="bill-back" onClick={backToNote}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Volver a la nota')}
      </button>

      {/* Cabecera: qué se va a pagar */}
      <div className="bill-card bill-card--pad">
        <div className="bill-doc-head">
          <div>
            <div className="bill-doc-head__title">
              <h2>{t('Pagar nota #')}{note.folio ?? '—'}</h2>
            </div>
            <div className="bill-doc-head__meta">
              {t(CHARGE_MODE_LABELS[note.chargeMode] ?? note.chargeMode)} · {t('Periodo:')}{' '}
              <strong>{formatDate(note.periodStart)}</strong> →{' '}
              <strong>{formatDate(note.periodEnd)}</strong>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="bill-summary__label">{t('Saldo a pagar')}</div>
            <div className="bill-summary__value bill-summary__value--accent">
              {formatCurrency(amountDue, note.currency)}
            </div>
            {amountPaid > 0 && (
              <div className="bill-hint">
                {t('Total de la nota')}: {formatCurrency(note.totalAmount, note.currency)} ·{' '}
                {t('Pagado')}: {formatCurrency(amountPaid, note.currency)}
              </div>
            )}
          </div>
        </div>
      </div>

      {resolved ? (
        <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
          <EmptyState
            icon={isInReview ? <Hourglass /> : <ShieldCheck />}
            title={
              isPaid
                ? t('Esta nota ya está pagada')
                : isInReview
                  ? t('Tu pago está en revisión')
                  : nothingToPay
                    ? t('No queda saldo por pagar')
                    : t('Esta nota está anulada')
            }
            description={
              isPaid
                ? t('No es necesario volver a pagarla.')
                : isInReview
                  ? t('Informaste un pago. Tu operador lo revisará y confirmará.')
                  : nothingToPay
                    ? t('El saldo de esta nota ya fue cubierto.')
                    : t('Una nota anulada no puede pagarse. Consulta con tu operador.')
            }
            action={
              isInReview ? (
                <Button variant="primary" onClick={() => navigate(`/home/pagos/${id}/resultado`)}>
                  {t('Ver estado del pago')}
                </Button>
              ) : (
                <Button variant="outline" onClick={backToNote}>
                  {t('Volver a la nota')}
                </Button>
              )
            }
          />
        </div>
      ) : (
        <>
          {/* Selección de método */}
          <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
            <h3 className="bill-card__title" style={{ marginBottom: '0.875rem' }}>
              {t('¿Cómo quieres pagar?')}
            </h3>

            {methods.length === 0 ? (
              <EmptyState
                icon={<CreditCard />}
                title={t('Aún no hay métodos de pago disponibles')}
                description={t('Tu operador todavía no ha habilitado métodos de pago. Escríbele para coordinar el pago de esta nota.')}
              />
            ) : (
              <div
                className="flex flex-col gap-3"
                role="radiogroup"
                aria-label={t('Métodos de pago disponibles')}
              >
                {methods.map((m) => {
                  const value = methodId(m);
                  return (
                    <MethodCard
                      key={value}
                      method={m}
                      selected={selected === value}
                      onSelect={setSelected}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Detalle del método elegido + acción */}
          {selectedMethod && (
            <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
              {isGateway && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg bg-status-info-soft"
                  style={{ marginBottom: '1rem' }}
                >
                  <ShieldCheck
                    className="w-5 h-5 shrink-0"
                    style={{ color: 'rgb(var(--status-info))' }}
                    aria-hidden
                  />
                  <p className="text-sm text-fg-muted" style={{ margin: 0 }}>
                    {t('El pago lo procesa')} <strong className="text-fg">{selectedMeta.label}</strong>.
                    {t('Beartrack solo te conecta con el medio de pago: no procesamos ni guardamos los datos de tu tarjeta o cuenta, y los fondos van directo a tu operador.')}
                  </p>
                </div>
              )}

              {isCash && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg bg-status-info-soft"
                  style={{ marginBottom: '1rem' }}
                >
                  <Hourglass
                    className="w-5 h-5 shrink-0"
                    style={{ color: 'rgb(var(--status-info))' }}
                    aria-hidden
                  />
                  <p className="text-sm text-fg-muted" style={{ margin: 0 }}>
                    {t('El pago en efectivo queda')} <strong className="text-fg">{t('sujeto a la confirmación')}</strong> {t('de tu operador. Te avisaremos cuando lo confirme.')}
                  </p>
                </div>
              )}

              {selectedMethod.type === 'manual_transfer' && (
                <p className="text-sm text-fg-muted" style={{ marginTop: 0, marginBottom: '1rem' }}>
                  {t('En el siguiente paso verás los datos bancarios de tu operador y podrás subir tu comprobante de transferencia.')}
                </p>
              )}

              {needsEmail && (
                <div className="bill-field" style={{ marginBottom: '1rem' }}>
                  <label className="bill-label" htmlFor="pay-email">
                    {t('Correo para el comprobante')}
                  </label>
                  <Input
                    id="pay-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tucorreo@empresa.cl"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 flex-wrap">
                <Button variant="outline" onClick={backToNote}>
                  {t('Cancelar')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleContinue}
                  loading={isCash ? submittingTransfer : saving}
                  disabled={!selected}
                >
                  {isGateway
                    ? t('Continuar al pago')
                    : isCash
                      ? t('Informar pago en efectivo')
                      : t('Continuar')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentMethodSelect;
